/**
 * ReadIndicator.js
 * Talkroute
 *
 * Created by Vel Genov on 2/13/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import { domIds } from "@constants/testConstants";
import React from "react";

const indicatorColors = {
  unread: "orange",
  hovering: "grey",
};

interface IReadIndicatorProps {
  messageRead?: boolean;
  allowHover?: boolean;
  // If the component is used in voicemail detail header, its value is true.
  isHeader?: boolean;
  disabled?: boolean;
  toggleCallBack: () => any;
  indicatorStyle?: string;
}

/**
 * Display the read indicator icon. Handle hover and click events on it
 *
 * @param toggleCallBack the callback function when the icon is clicked on
 * @param messageRead a boolean to indicate if the message was read
 */
const ReadIndicator = ({
  messageRead,
  allowHover,
  isHeader,
  disabled,
  toggleCallBack,
  indicatorStyle,
}: IReadIndicatorProps) => {
  /**
   * Render the body of the message read icon
   *
   * @param toggleCallBack the callback function when the icon is clicked on
   * @param messageRead a boolean to indicate if the message was read
   */
  const showMessageReadBody = (props) => {
    return (
      <div
        id={domIds.VOICEMAIL_READSTATUS_BUTTON}
        className={indicatorStyle}
        style={{ height: "100%", display: "inline-block" }}
        onClick={() => toggleCallBack()}
        onKeyDown={() => toggleCallBack()}
        role="presentation"
      >
        {showIcon(props)}
      </div>
    );
  };

  /**
   * Determine the type of a read indicator icon to show based on the props
   *
   * @param messageRead a boolean to indicate if the message was read
   */
  const showIcon = (props) => {
    if (disabled) {
      return drawIconOutline();
    }
    if (messageRead === false) {
      // The message is unread. Show the orange icon
      return drawIcon(indicatorColors.unread);
    }
    if (allowHover === true) {
      // The message is read and the user is hovering over it. Show the gray icon
      return drawIcon(indicatorColors.hovering);
    }

    return drawIconOutline();
  };

  /**
   * Draw an outline for the read indicator icon in grey if it is in header,
   * otherwise, draw an empty element
   */
  const drawIconOutline = () => {
    if (isHeader) {
      return (
        <span id={domIds.VOICEMAIL_READ_ICON} className="dot-unselected" style={{ opacity: disabled ? .45 : 1, pointerEvents: "none" }} />
      );
    }

    return (
      <div
        id={domIds.VOICEMAIL_READ_ICON}
        style={{ width: "11px", height: "1em" }}
      />
    );
  };

  /**
   * Draw the read indicator icon
   *
   * @param color the color to draw the icon in
   */
  const drawIcon = (color) => {
    let className;
    if (color === indicatorColors.unread) {
      className = isHeader ? "dot-selected" : "dot-selected-small";
    } else {
      className = isHeader ? "dot-hover" : "dot-hover-small";
    }

    return (
      <span
        id={
          color === indicatorColors.unread
            ? domIds.VOICEMAIL_UNREAD_ICON
            : domIds.VOICEMAIL_READ_ICON
        }
        className={className}
      />
    );
  };

  return showMessageReadBody({ messageRead, toggleCallBack });
};

export default ReadIndicator;
